import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import SubmitButton from '../../../components/form/SubmitButton';
import AddOfferForm, { OfferFormValues } from '../../../components/store/offers/form/AddOfferForm';
import MainTitle from '../../../components/ui/MainTitle';
import { useProductCategories } from '../../../hooks/store/productCategory';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStoreProducts } from '../../../hooks/store/product';
import { useUpdateResource } from '../../../hooks/useUpdateResource';
import { useTranslation } from 'react-i18next';
import LoadingButton from '../../../components/form/LoadingButton';

const AddOfferPage: React.FC = () => {

    const { storeId} = useParams<{ storeId: string}>();
    const numericStoreId = storeId ? parseInt(storeId) : undefined;

  const {data:categories} = useProductCategories(numericStoreId);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<OfferFormValues>({
    defaultValues: {
      type: 'fixed',
      durationUnit: 'days',
      target: 'all',
      productIds: [],
      categoryIds: [],
    }
  });

  const watchType = watch('type');
  const watchTarget = watch('target');
  const watchDurationUnit = watch('durationUnit');

  const [selectedProducts, setSelectedProducts] = useState<{ id: number}[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<{ id: number;}[]>([]);
  const [searchParams,setSearchParams] = useSearchParams();
  const pageQuery = Number(searchParams.get('page')) || 1;


    const handlePagination = (page: number) => {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('page', page.toString());
          setSearchParams(newParams);
      };
      const {data:products  } = useStoreProducts({page:pageQuery,limit:8,storeId:numericStoreId});
 

  const onSelectProduct = (product: { id: number }) => {
    setSelectedProducts(prev => [...prev, product]);
  };

  const onRemoveProduct = (id: number) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== id));
  };

  const onSelectCategory = (category: { id: number }) => {
    setSelectedCategories(prev => [...prev, category]);
  };

  const onRemoveCategory = (id: number) => {
    setSelectedCategories(prev => prev.filter(c => c.id !== id));
  };

  const {loading,updateResource} = useUpdateResource();
  const navigate = useNavigate();
  const {t} = useTranslation();

  const onSubmit: SubmitHandler<OfferFormValues> = (data) => {
    const payload: any = {
      name: data.name,
      type: data.type,
      duration: data.duration,
      durationUnit: data.durationUnit,
      startDate: new Date(data.startDate).toISOString(),
      target: data.target,
    };

    if (data.type === 'fixed') payload.discountAmount = data.discountAmount;
    if (data.type === 'percentage') payload.discountPercentage = data.discountPercentage;
    if (data.type === 'incentive') {
      payload.buyQty = data.buyQty;
      payload.getFreeQty = data.getFreeQty;
    }

    if (data.target === 'product') payload.productIds = selectedProducts.map(p => p.id);
    if (data.target === 'category') payload.categoryIds = selectedCategories.map(c => c.id);

    const endPoint = numericStoreId ? `offer?storeId=${numericStoreId}` : `offer` 
    updateResource(endPoint, payload, numericStoreId?"admin" :"store",{
    method: "POST",
    successMessage: t("createdSuccess"),
    invalidateQueries:["store-offers"],
    navigateAfter: () => navigate(numericStoreId ?`/admin/stores/${numericStoreId}/offers` :`/store/offers`),
  });
  };

  return (
    <div className="container p-6 h-[100vh] overflow-auto">
      <div className="mb-14">
        <MainTitle title="add_new_offer"/>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <AddOfferForm
          register={register}
          errors={errors}
          watchType={watchType}
          watchTarget={watchTarget}
          watchDurationUnit={watchDurationUnit}
          setValue={setValue}
          selectedProducts={selectedProducts}
          selectedCategories={selectedCategories}
          availableProducts={products ? products.data : []}
          availableCategories={categories? categories?.map(c=>({id:c.id , name:c.languages[0].title})):[]}
          onSelectProduct={onSelectProduct}
          onRemoveProduct={onRemoveProduct}
          onSelectCategory={onSelectCategory}
          onRemoveCategory={onRemoveCategory}
          currentPage={pageQuery}
          totalPages={products?.totalPages||0}
          handlePagination={handlePagination}
        />

        <div className="mt-6">
          {
            loading?
            <LoadingButton/>
            :
            <SubmitButton label='create'/>
          }
        </div>
      </form>
    </div>
  );
};

export default AddOfferPage;
